class StorePicker extends HTMLElement {
    constructor() {
        super();
        this.onBodyClick = this.handleBodyClick.bind(this);
        this.onKeyup = this.handleKeyup.bind(this);
        this.elements = {
            selected: this.querySelector('strong'),
            details: this.querySelector('details'),
            summary: this.querySelector('summary')
        };
        // Store locations will be populated from the global storeLocations array
        this.storeLocations = [];
        // Added property to store original order
        this.originalStoreOrder = [];
        // Track current search location (use LocationUtils as single source of truth)
        this.currentSearchLocation = LocationUtils.getZipCode() || '';
        // Track if geolocation is in progress
        this.geoLocationInProgress = false;
        // Add flag to track if this is an initial load/auto request
        this.isInitialLoad = true;

        // Only add click listener if we're actually on a page with store picker elements
        if (document.querySelector('store-picker')) {
            document.body.addEventListener('click', this.onBodyClick);
        }

        // console.log('Store Picker initialized');
        // Add location features outside the dropdown
        this.addExternalLocationFeatures();

        // Then try to initialize with store locator data
        this.initWithStoreLocatorData();
    }
    
    connectedCallback() {
        // If we have a saved location, load it
        this.loadSavedLocation();

        // After a brief delay to ensure the DOM is ready, request geolocation if needed
        setTimeout(() => {
            this.autoRequestGeolocation();
            // Move buttons below addresses
            this.moveButtonsBelowAddress();

            // After initialization is complete, set initialLoad to false
            setTimeout(() => {
                this.isInitialLoad = false;
            }, 1000);
        }, 500);
    }

    // Cleanup when component is removed from DOM - prevents memory leaks
    disconnectedCallback() {
        // Remove body click listener to prevent memory leak
        document.body.removeEventListener('click', this.onBodyClick);
        // Remove keyup listener if it was added
        document.body.removeEventListener('keyup', this.onKeyup);
    }

    /**
     * Moves all store selection buttons below the address for each store
     */
    moveButtonsBelowAddress() {
        // Find all store items in the list
        const storeItems = this.querySelectorAll('li.store-picker__store');
        
        storeItems.forEach(item => {
            const buttonElement = item.querySelector('.store-fav-selector');
            const addressElement = item.querySelector('.store-picker__store-address');
             
            // Only proceed if both elements exist
            if (buttonElement && addressElement) {
                // Remove the button from its current location
                buttonElement.parentNode.removeChild(buttonElement);
                
                // Add the button after the address element
                addressElement.insertAdjacentElement('afterend', buttonElement);
                
            }
        });
    }

    // Auto-request geolocation on page load if needed
    autoRequestGeolocation() {
        // Check if store data is available
        if (!window.storeLocations || window.storeLocations.length === 0) {
            // console.log('Store data not yet available, delaying auto-geolocation...');
            // Retry after a delay
            setTimeout(() => this.autoRequestGeolocation(), 500);
            return;
        }
        
        // Only auto-request if:
        // 1. We don't have a saved location
        // 2. Geolocation is supported
        // 3. We're not already in progress with a geolocation request
        const location = LocationUtils.getLocation();
        const savedLocation = location?.zipCode;
        const savedCoordinates = location?.coordinates;

        if (!savedLocation && !this.geoLocationInProgress && navigator.geolocation) {
            // console.log('Auto-requesting geolocation permission...');
            this.getUserLocation(true); // true indicates this is an auto-request
        }
        // If we have saved coordinates but no search performed yet, use those
        else if (savedCoordinates && !this.locationSearchPerformed) {
            if (savedCoordinates.latitude && savedCoordinates.longitude) {
                // console.log('Using saved coordinates for initial search', savedCoordinates);
                this.findNearestStore(savedCoordinates.latitude, savedCoordinates.longitude, true); // true = auto request
                this.locationSearchPerformed = true;
            }
        }
    }

    // Load saved location and perform search if needed
    loadSavedLocation() {
        const location = LocationUtils.getLocation();
        const savedLocation = location?.zipCode;
        const savedLocationType = location?.source || 'zip';
        const savedCoordinates = location?.coordinates;

        if (savedLocation) {
            // console.log(`Found saved location: ${savedLocation}, type: ${savedLocationType}`);
            this.currentSearchLocation = savedLocation;
            this.updateLocationDisplay();
            this.locationSearchPerformed = false;

            // If the saved location is a ZIP code, perform a search with it
            if (savedLocationType === 'zip' && /^\d{5}$/.test(savedLocation)) {
                // Wait a bit for the page to load and then perform search
                setTimeout(() => {
                    // console.log('Performing search with saved ZIP:', savedLocation);
                    // Use the geocodeAddress function from store-locator.js
                    if (typeof window.geocodeAddress === 'function') {
                        window.geocodeAddress(savedLocation + ', USA', (loc) => {
                            if (loc) {
                                this.findNearestStore(loc.lat, loc.lng, true); // true = auto request
                                this.locationSearchPerformed = true;
                            } else {
                                // console.error('Failed to geocode saved ZIP:', savedLocation);
                                // Fallback to simple ZIP search
                                this.simpleZipFallback(savedLocation);
                                this.locationSearchPerformed = true;
                            }
                        });
                    } else {
                        // console.log('Geocoding not available, using fallback for saved ZIP');
                        // Fallback to simple ZIP search
                        this.simpleZipFallback(savedLocation);
                        this.locationSearchPerformed = true;
                    }
                }, 500);
            }
            // If we have saved coordinates, use those instead of requesting geolocation again
            else if (savedLocationType === 'geo' && savedCoordinates) {
                if (savedCoordinates.latitude && savedCoordinates.longitude) {
                    setTimeout(() => {
                        // console.log('Using saved coordinates', savedCoordinates);
                        this.findNearestStore(savedCoordinates.latitude, savedCoordinates.longitude, true); // true = auto request
                        this.locationSearchPerformed = true;
                    }, 500);
                }
            }
        }
    }

    // Set the current location and save it to localStorage
    setCurrentLocation(location, type = 'zip', coordinates = null) {
        this.currentSearchLocation = location;

        // Use LocationUtils for storage and event dispatch
        LocationUtils.setLocation(location, type, coordinates);

        this.updateLocationDisplay();
    }

    // Clear the saved location
    clearSavedLocation() {
        this.currentSearchLocation = '';

        // Use LocationUtils for storage and event dispatch
        LocationUtils.clearLocation();

        this.updateLocationDisplay();
    }

    // Initialize with data from store locator
    initWithStoreLocatorData() {
        // console.log('Looking for store locator data...');

        // Check if global storeLocations array exists (from store-locator.js)
        if (typeof window.storeLocations !== 'undefined' && window.storeLocations.length > 0) {
            // console.log(`Found ${window.storeLocations.length} stores in global storeLocations array`);
            this.processStoreLocatorData();
        } else {
            // Try to find the storeLocations variable in any scope
            // console.log('Global storeLocations not found, trying alternate methods...');

            // Check if there's a variable called storeLocations in the current scope
            if (typeof storeLocations !== 'undefined') {
                window.storeLocations = storeLocations;
                // console.log('Found storeLocations in current scope, processing...');
                this.processStoreLocatorData();
            } else {
                // As a last resort, try to extract store data from the page
                // console.log('No storeLocations array found. Will use basic functionality.');

                // Attempt to use store picker data only
                this.extractStoreDataFromPicker();
            }
        }
    }

    // Process store locator data and match with store picker items
    processStoreLocatorData() {
        // Get all store items in the picker
        const storeItems = this.querySelectorAll('li[data-store-id]');
        // console.log(`Found ${storeItems.length} store items in picker`);

        if (storeItems.length === 0) {
            // console.log('No store items found in picker, using locator data directly');

            // If no store items in picker, use locator data directly
            window.storeLocations.forEach(store => {
                if (store.coordinates) {
                    this.storeLocations.push({
                        id: store.id,
                        name: store.name,
                        address: store.address || '',
                        city: store.city || '',
                        state: store.state || '',
                        zip: store.zip || '',
                        coordinates: {
                            lat: store.coordinates.lat,
                            lng: store.coordinates.lng
                        }
                    });
                }
            });
        } else {
            // Map store locator data to store picker items
            storeItems.forEach(item => {
                const storeId = item.dataset.storeId;
                const storeName = item.dataset.storeName;

                // Try to find a matching store in the global storeLocations array
                let matchedStore = this.findMatchingStore(storeId, storeName);

                if (matchedStore) {
                    // console.log(`Matched store "${storeName}" with store locator data`);

                    // Store the coordinates and other relevant info
                    item.dataset.latitude = matchedStore.coordinates.lat;
                    item.dataset.longitude = matchedStore.coordinates.lng;

                    // Add to our internal store locations array
                    this.storeLocations.push({
                        id: storeId,
                        name: storeName,
                        address: matchedStore.address || '',
                        city: matchedStore.city || '',
                        state: matchedStore.state || '',
                        zip: matchedStore.zip || '',
                        coordinates: {
                            lat: matchedStore.coordinates.lat,
                            lng: matchedStore.coordinates.lng
                        }
                    });
                } else {
                    // console.log(`No match found for store "${storeName}" in store locator data`);
                }
            });
        }

        // console.log(`Processed ${this.storeLocations.length} stores with coordinates`);
    }

    // Extract store data from picker without store locator
    extractStoreDataFromPicker() {
        const storeItems = this.querySelectorAll('li[data-store-id]');
        // console.log(`Found ${storeItems.length} store items in picker`);

        storeItems.forEach(item => {
            const storeId = item.dataset.storeId;
            const storeName = item.dataset.storeName;

            // Check if the item already has coordinates
            if (item.dataset.latitude && item.dataset.longitude) {
                this.storeLocations.push({
                    id: storeId,
                    name: storeName,
                    coordinates: {
                        lat: parseFloat(item.dataset.latitude),
                        lng: parseFloat(item.dataset.longitude)
                    }
                });
            }
        });
    }
    // Find a matching store from the global storeLocations array
    // Find a matching store from the global storeLocations array
    findMatchingStore(storeId, storeName) {
        // Helper function to convert "Super-Sod of Birmingham" to "super-sod-birmingham" format
        const convertPickerNameToLocatorId = (name) => {
            // Remove "of" and convert to lowercase with hyphens
            return name.toLowerCase()
                .replace(/\s+of\s+/g, '-')  // Replace " of " with "-"
                .replace(/[^a-z0-9-]/g, '')   // Remove non-alphanumeric except hyphens
                .replace(/--+/g, '-')         // Replace multiple hyphens with single hyphen
                .replace(/^-|-$/g, '');       // Remove leading/trailing hyphens
        };

        // First try direct ID match
        let matchedStore = window.storeLocations.find(store => {
            return store.id === storeId;
        });

        // If no match by ID, try converting picker name to locator ID format
        if (!matchedStore) {
            const convertedId = convertPickerNameToLocatorId(storeName);
            // console.log(`Trying to match "${storeName}" as "${convertedId}"`);

            matchedStore = window.storeLocations.find(store => {
                return store.id === convertedId;
            });
        }

        // If no match by converted ID, try normalized name matching
        if (!matchedStore) {
            const normalizeName = name => name.toLowerCase().replace(/[^a-z0-9]/g, '');
            const normalizedStoreName = normalizeName(storeName);

            matchedStore = window.storeLocations.find(store => {
                return normalizeName(store.name) === normalizedStoreName;
            });
        }

        // If still no match, try extracting city name from picker name and matching
        if (!matchedStore) {
            // Extract city from "Super-Sod of CityName" format
            const cityMatch = storeName.match(/Super-Sod\s+of\s+(.+)/i);
            if (cityMatch) {
                const cityName = cityMatch[1].trim();

                matchedStore = window.storeLocations.find(store => {
                    // Check if store ID contains the city name
                    const normalizedCity = cityName.toLowerCase().replace(/[^a-z0-9]/g, '');
                    const normalizedStoreId = store.id.toLowerCase().replace(/[^a-z0-9]/g, '');
                    return normalizedStoreId.includes(normalizedCity) ||
                        (store.city && store.city.toLowerCase().replace(/[^a-z0-9]/g, '') === normalizedCity);
                });

                if (matchedStore) {
                    // console.log(`Found match for "${storeName}" by city name "${cityName}": "${matchedStore.name}"`);
                }
            }
        }

        // Special case handling for specific mismatches
        if (!matchedStore) {
            // Map of special cases: picker name -> locator id
            const specialCases = {
                'Super-Sod of Hendersonville': 'super-sod-hendersonville',
                'Super-Sod of Birmingham': 'super-sod-birmingham',
                'Super-Sod of Manchester': 'super-sod-manchester',
                'Super-Sod of Opelika': 'super-sod-opelika',
                'Super-Sod of Cartersville': 'super-sod-cartersville',
                'Super-Sod of Benton': 'super-sod-benton',
                'Super-Sod of Newnan': 'super-sod-newnan',
                'Super-Sod of Marietta': 'super-sod-marietta',
                'Super-Sod of Canton': 'super-sod-canton',
                'Super-Sod of Knoxville': 'super-sod-knoxville',
                'Super-Sod of Forest Park': 'super-sod-forest-park',
                'Super-Sod of Alpharetta': 'super-sod-alpharetta',
                'Super-Sod of Dawsonville': 'super-sod-dawsonville',
                'Super-Sod of Mooresville': 'super-sod-mooresville',
                'Super-Sod of Orangeburg': 'super-sod-orangeburg',
                'Super-Sod of Cary': 'super-sod-cary',
                'Super-Sod of Raleigh': 'super-sod-raleigh',
                'Super-Sod of Middle Georgia': 'super-sod-middle-georgia',
                'Super-Sod of Nashville': 'super-sod-nashville',
                'Super-Sod of Gwinnett': 'super-sod-gwinnett',
                'Super-Sod of Charleston': 'super-sod-charleston',
                'Super-Sod of Charlotte': 'super-sod-charlotte',
                'Super-Sod of Greenville': 'super-sod-greenville',
                'Super-Sod of Columbia': 'super-sod-columbia'
            };

            if (specialCases[storeName]) {
                matchedStore = window.storeLocations.find(store => {
                    return store.id === specialCases[storeName];
                });

                if (matchedStore) {
                    // console.log(`Found match for "${storeName}" using special case mapping`);
                }
            }
        }

        // Final fallback: partial name match
        if (!matchedStore) {
            const partialNameMatch = window.storeLocations.find(store => {
                return storeName.toLowerCase().includes(store.city.toLowerCase()) ||
                    store.name.toLowerCase().includes(storeName.toLowerCase());
            });

            if (partialNameMatch) {
                // console.log(`Found partial name match for "${storeName}": "${partialNameMatch.name}"`);
                matchedStore = partialNameMatch;
            }
        }

        return matchedStore;
    }


    // Add location features outside the dropdown, next to location-settings__content
    addExternalLocationFeatures() {
        // console.log('Adding external location features');

        // Find the location-settings__content parent
        const storePicker = this;
        const parentElement = this.closest('.location-settings__content');

        if (!parentElement) {
            // console.error('Could not find location-settings__content parent');
            return;
        }

        // Create location features container
        const locationFeatures = document.createElement('div');
        locationFeatures.className = 'location-finder';

        locationFeatures.innerHTML = `
            <div class="location-finder__body">
                <div class="location-finder__buttons">
                    <button type="button" class="use-my-location-button" style="width: 100%; display: flex; align-items: center; justify-content: center; gap: 8px; padding: 8px 10px; font-size: 12px;">
                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <circle cx="12" cy="12" r="10"></circle>
                            <circle cx="12" cy="12" r="1"></circle>
                            <line x1="12" y1="2" x2="12" y2="4"></line>
                            <line x1="12" y1="20" x2="12" y2="22"></line>
                            <line x1="2" y1="12" x2="4" y2="12"></line>
                            <line x1="20" y1="12" x2="22" y2="12"></line>
                        </svg>
                    </button>
                </div>
                <div class="location-finder__zip-search">
                    <div class="zip-search-form" style="display: flex; gap: 5px;">
                        <div class="field" style="flex-grow: 1;">
                            <input type="text" 
                                  id="zipCodeSearch-${Date.now()}" 
                                  class="field__input zip-code-input" 
                                  placeholder="Enter ZIP Code" 
                                  pattern="[0-9]{5}" 
                                  maxlength="5"
                                  >
                        </div>
                        <button type="button" class="btn btn--secondary zip-search-button" style="white-space: nowrap; padding: 8px; font-size: 12px;">
                            Set Store
                        </button>
                    </div>
                </div>
            </div>
        `;

        // Insert after the store-picker
        parentElement.appendChild(locationFeatures);

        // Add error message container
        const errorContainer = document.createElement('div');
        errorContainer.className = 'location-error';
        locationFeatures.appendChild(errorContainer);

        // Store direct references to the elements we'll need later
        let zipInputElement, zipSearchButtonElement;

        // Small delay to ensure DOM is updated before we try to find elements
        setTimeout(() => {
            // Get element references after DOM has been updated
            zipInputElement = locationFeatures.querySelector('.zip-code-input');
            zipSearchButtonElement = locationFeatures.querySelector('.zip-search-button');
            const useLocationButton = locationFeatures.querySelector('.use-my-location-button');

            // Add event listeners for "use my location" button
            if (useLocationButton) {
                useLocationButton.addEventListener('click', () => {
                    // Open the dropdown first for better UX
                    this.open = true;
                    // Then use location
                    this.getUserLocation(false); // false indicates this is a manual request
                });
            }

            // Add event listener for ZIP search button
            if (zipSearchButtonElement) {
                // console.log('Adding click listener to ZIP search button');
                zipSearchButtonElement.addEventListener('click', () => {
                    // console.log('ZIP search button clicked');
                    // Get the input element using class selector
                    const zipInput = locationFeatures.querySelector('.zip-code-input');
                    if (zipInput && zipInput.value && zipInput.value.trim()) {
                        // console.log('ZIP input found, value:', zipInput.value);
                        // Open the dropdown first for better UX
                        this.open = true;
                        // Then search by zip, passing the value directly
                        this.searchByZipCode(zipInput.value.trim());
                    } else {
                        // console.error('ZIP input field not found or empty on button click');
                        this.showLocationError("Please enter a ZIP code");
                    }
                });
            } else {
                // console.error('ZIP search button not found');
            }

            // Add event listener for ZIP input field
            if (zipInputElement) {
                // console.log('Adding keypress listener to ZIP input field');
                zipInputElement.addEventListener('keypress', (e) => {
                    if (e.key === 'Enter') {
                        // Use the current target since we're in the event handler
                        const zipInput = e.target;
                        if (zipInput && zipInput.value && zipInput.value.trim()) {
                            // console.log('Enter key pressed in ZIP input, value:', zipInput.value);
                            // Open the dropdown first for better UX
                            this.open = true;
                            // Then search by zip, passing the value directly
                            this.searchByZipCode(zipInput.value.trim());
                        } else {
                            // console.error('ZIP input field not found or empty on Enter key');
                            this.showLocationError("Please enter a ZIP code");
                        }
                    }
                });

                // Set zip input value if we have a saved ZIP
                const location = LocationUtils.getLocation();
                const savedLocation = location?.zipCode;
                const savedLocationType = location?.source || 'zip';

                if (savedLocation && /^\d{5}$/.test(savedLocation)) {
                    zipInputElement.value = savedLocation;
                }
            } else {
                // console.error('ZIP input field not found during event setup');
            }
        }, 100);

        // console.log('External location features added successfully');
        
        // Create the location display div
        this.createLocationDisplayElement();
    }
    
    // Method to create or update the location display element
    createLocationDisplayElement() {
        const parentElement = this.closest('.location-settings');
        if (!parentElement) return;
        
        // Check if location display already exists
        let locationDisplay = parentElement.querySelector('.store-location-display');
        
        // If it doesn't exist, create it
        if (!locationDisplay) {
            locationDisplay = document.createElement('div');
            locationDisplay.className = 'store-location-display';
            locationDisplay.style.cssText = 'margin-top: 5px; margin-left: 0; font-size: 12px; color: #666; display: none;';
            
            // Insert it after the summary element
            const summaryElement = this.querySelector('summary');
            if (summaryElement) {
                const summaryParent = summaryElement.parentElement;
                if (summaryParent) {
                    summaryParent.insertAdjacentElement('afterend', locationDisplay);
                } else {
                    parentElement.appendChild(locationDisplay);
                }
            } else {
                parentElement.appendChild(locationDisplay);
            }
        }
        
        // Update the initial display if we have a saved location
        this.updateLocationDisplay();
    }
    
    // Method to update the location display
    updateLocationDisplay() {
        const locationDisplay = document.querySelector('.store-location-display');
        if (!locationDisplay) return;
        
        if (this.currentSearchLocation) {
            locationDisplay.textContent = `Showing stores near ${this.currentSearchLocation}`;
            locationDisplay.style.display = 'none';
        } else {
            locationDisplay.style.display = 'none';
        }
    }
    
    
    /**
     * Get user location with simple geolocation
     * @param {boolean} isAutoRequest - Whether this is an automatic request on page load
     */
    getUserLocation(isAutoRequest = false) {
        // console.log('[LOCATION] getUserLocation called');
        
        if (this.geoLocationInProgress) {
            // console.log('[LOCATION] Already in progress');
            return;
        }
        
        this.geoLocationInProgress = true;
        
        // Show loading state for manual requests
        if (!isAutoRequest) {
            this.setCurrentLocation('Getting location...', 'geo');
            
            const useLocationButton = document.querySelector('.use-my-location-button');
            if (useLocationButton) {
                useLocationButton.innerHTML = '<span class="loading-spinner" style="display: inline-block; width: 12px; height: 12px; border: 2px solid rgba(0,0,0,0.1); border-left-color: currentColor; border-radius: 50%; animation: spinner 0.75s linear infinite;"></span> Locating...';
                useLocationButton.disabled = true;
            }
            
            this.open = true;
        }

        // Try browser geolocation
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const lat = position.coords.latitude;
                    const lng = position.coords.longitude;
                    // console.log(`Got coordinates: ${lat}, ${lng}`);

                    // Store coordinates temporarily - don't dispatch location:changed yet
                    // The actual ZIP code will be set in findNearestStore() after we find the nearest store
                    this.pendingGeoCoordinates = { latitude: lat, longitude: lng };

                    // Find nearest using actual distance calculation
                    this.findNearestStore(lat, lng, isAutoRequest);

                    // Reset button
                    if (!isAutoRequest) {
                        this.resetLocationButton(document.querySelector('.use-my-location-button'));
                    }
                    
                    this.geoLocationInProgress = false;
                    
                    // Keep dropdown open for manual requests
                    if (!isAutoRequest) {
                        this.open = true;
                    }
                },
                (error) => {
                    // console.error('Geolocation failed:', error);
                    this.showLocationError("Unable to get your location. Please enter your ZIP code.");
                    
                    if (!isAutoRequest) {
                        this.resetLocationButton(document.querySelector('.use-my-location-button'));
                    }
                    
                    this.geoLocationInProgress = false;
                },
                { timeout: 10000 }
            );
        } else {
            this.showLocationError("Your browser doesn't support geolocation. Please enter your ZIP code.");
            this.geoLocationInProgress = false;
        }
    }
    
    
    /**
     * Helper to reset location button
     */
    resetLocationButton(button) {
        if (button) {
            button.innerHTML = `
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <circle cx="12" cy="12" r="1"></circle>
                    <line x1="12" y1="2" x2="12" y2="4"></line>
                    <line x1="12" y1="20" x2="12" y2="22"></line>
                    <line x1="2" y1="12" x2="4" y2="12"></line>
                    <line x1="20" y1="12" x2="22" y2="12"></line>
                </svg>
                Use My Location
            `;
            button.disabled = false;
        }
    }

    /**
     * Find nearest store and handle results
     * @param {number} userLat - User latitude
     * @param {number} userLng - User longitude
     * @param {boolean} isAutoRequest - Whether this is from an automatic request
     */
    findNearestStore(userLat, userLng, isAutoRequest = false) {
        // console.log(`Finding stores near coordinates: ${userLat}, ${userLng}, autoRequest: ${isAutoRequest}`);

        // Validate inputs
        if (typeof userLat !== 'number' || typeof userLng !== 'number' || isNaN(userLat) || isNaN(userLng)) {
            // console.error('Invalid coordinates:', userLat, userLng);
            this.showLocationError("Invalid location coordinates received.");
            return;
        }

        // Always use window.storeLocations as the primary source
        if (typeof window.storeLocations !== 'undefined' && window.storeLocations.length > 0) {
            // console.log('Using global storeLocations array with', window.storeLocations.length, 'stores');
            this.storeLocations = window.storeLocations.map(store => ({
                id: store.id,
                name: store.name,
                address: store.address || '',
                city: store.city || '',
                state: store.state || '',
                zip: store.zip || '',
                coordinates: {
                    lat: parseFloat(store.coordinates.lat),
                    lng: parseFloat(store.coordinates.lng)
                }
            }));
        } else {
            // console.error('No store locations available');
            this.showLocationError("Store location data is not available.");
            return;
        }

        // console.log(`Calculating distances for ${this.storeLocations.length} stores...`);

        // Calculate distance to each store
        const storesWithDistance = this.storeLocations.map(store => {
            // Validate store coordinates
            if (!store.coordinates ||
                typeof store.coordinates.lat !== 'number' ||
                typeof store.coordinates.lng !== 'number' ||
                isNaN(store.coordinates.lat) ||
                isNaN(store.coordinates.lng)) {
                // console.warn(`Store ${store.name} has invalid coordinates:`, store.coordinates);
                return { ...store, distance: Infinity }; // Use Infinity to sort this to the end
            }

            const distance = LocationUtils.calculateDistance(
                userLat, userLng,
                store.coordinates.lat,
                store.coordinates.lng
            );

            return {
                ...store,
                distance: distance
            };
        });

        // Filter out stores with invalid distances
        const validStores = storesWithDistance.filter(store => store.distance !== Infinity);

        if (validStores.length === 0) {
            // console.error('No stores with valid distances found');
            this.showLocationError("We couldn't find any stores near your location.");
            return;
        }

        // Sort by distance
        validStores.sort((a, b) => a.distance - b.distance);

        // console.log('Nearest stores:', validStores.slice(0, 3).map(s => `${s.name}: ${s.distance.toFixed(2)} miles`));

        // Display results
        this.displayNearbyStores(validStores);

        // Automatically select the nearest store
        if (validStores.length > 0) {
            const nearestStore = validStores[0];
            console.log('[4] findNearestStore found nearest:', nearestStore.name, nearestStore.id);

            // Always update ZIP input with nearest store's ZIP code to show user what location was found
            if (nearestStore.zip) {
                const zipInput = document.querySelector('.zip-code-input');
                if (zipInput) {
                    zipInput.value = nearestStore.zip;
                }
            }

            // Only auto-select if this is a manual request or if no store is already selected
            console.log('[4] Condition check: isAutoRequest=', isAutoRequest, 'this.userStore=', this.userStore);
            console.log('[4] Condition result: !isAutoRequest=', !isAutoRequest, '|| !this.userStore=', !this.userStore, '=> will enter block:', (!isAutoRequest || !this.userStore));

            if (!isAutoRequest || !this.userStore) {
                this.userStore = nearestStore.id;

                // Use LocationUtils for storage and event dispatch
                console.log('[5] Calling LocationUtils.setStore with:', nearestStore.id, nearestStore.name, nearestStore.zip);
                LocationUtils.setStore(nearestStore.id, nearestStore.name, nearestStore.zip);

                // Only update location with store's ZIP if this was a geolocation request
                // (user clicked "Use My Location"). Don't overwrite user's manually entered ZIP.
                if (this.pendingGeoCoordinates && nearestStore.zip) {
                    LocationUtils.setLocation(nearestStore.zip, 'geo', this.pendingGeoCoordinates);
                    this.pendingGeoCoordinates = null; // Clear after use
                }

                // Close the dropdown after auto-selection (for both ZIP search and geolocation)
                this.open = false;
            }

            // Make sure the dropdown stays open to show results only if manual request (this is now overridden above)
            if (!isAutoRequest && !this.isInitialLoad && false) {
                this.open = true;
            }
        }
    }
    
    // Simple searchByZipCode method
    searchByZipCode(directValue) {
        console.log('[1] searchByZipCode called with:', directValue);

        // Clean the ZIP code
        const zipCode = directValue ? directValue.replace(/\D/g, '').substring(0, 5) : '';
        
        // Validate
        if (zipCode.length !== 5) {
            this.showLocationError("Please enter a valid 5-digit ZIP code");
            return;
        }
        
        // Show loading
        const zipSearchButton = document.querySelector('.zip-search-button');
        if (zipSearchButton) {
            zipSearchButton.innerHTML = '<span class="loading-spinner" style="display: inline-block; width: 12px; height: 12px; border: 2px solid rgba(0,0,0,0.1); border-left-color: currentColor; border-radius: 50%; animation: spinner 0.75s linear infinite;"></span> Searching...';
            zipSearchButton.disabled = true;
        }

        // Save location
        this.setCurrentLocation(zipCode, 'zip');

        // Find nearest store and update UI
        console.log('[2] Calling findNearestStoreByZip with:', zipCode);
        this.findNearestStoreByZip(zipCode);

        // Reset button after delay
        setTimeout(() => {
            if (zipSearchButton) {
                zipSearchButton.innerHTML = 'Set Store';
                zipSearchButton.disabled = false;
            }
        }, 500);
    }

    // Method to find nearest stores by ZIP code - mimicking store-locator.js
    findNearestStoreByZip(searchZip) {
        console.log('[3] findNearestStoreByZip called with:', searchZip);
        console.log('[3] storeLocations available:', window.storeLocations?.length || 0);
        console.log('[3] geocodeAddress available:', typeof window.geocodeAddress === 'function');
        console.log('[3] Google Maps available:', typeof google !== 'undefined' && google?.maps?.Geocoder);

        if (!window.storeLocations || window.storeLocations.length === 0) {
            console.log('[3] ERROR: No store locations available');
            this.showLocationError("Store location data is not available.");
            return;
        }

        // Use the same geocodeAddress function from store-locator.js if available
        if (typeof window.geocodeAddress === 'function') {
            console.log('[3] Using geocodeAddress function');
            window.geocodeAddress(searchZip + ', USA', (location) => {
                if (location) {
                    console.log('[3] Geocode success, calling findNearestStore with:', location);

                    // Save the geocoded coordinates so drawer sorting can use them
                    LocationUtils.setLocation(searchZip, 'zip', { lat: location.lat, lng: location.lng });

                    // Use the coordinates to find nearest stores
                    this.findNearestStore(location.lat, location.lng, false);
                } else {
                    console.log('[3] Geocoding failed, using fallback');
                    this.simpleZipFallback(searchZip);
                }
            });
        } else if (typeof google !== 'undefined' && google.maps && google.maps.Geocoder) {
            // Use Google Maps directly if available
            // console.log('Using Google Maps Geocoder directly');
            const geocoder = new google.maps.Geocoder();
            geocoder.geocode({ address: searchZip + ', USA' }, (results, status) => {
                if (status === 'OK' && results[0]) {
                    const location = {
                        lat: results[0].geometry.location.lat(),
                        lng: results[0].geometry.location.lng()
                    };
                    // console.log(`Got coordinates for ZIP ${searchZip}:`, location);

                    // Save the geocoded coordinates so drawer sorting can use them
                    LocationUtils.setLocation(searchZip, 'zip', { lat: location.lat, lng: location.lng });

                    this.findNearestStore(location.lat, location.lng, false);
                } else {
                    // console.log('Geocoding failed, using fallback');
                    this.simpleZipFallback(searchZip);
                }
            });
        } else {
            // No geocoding available, use simple fallback
            // console.log('No geocoding available, using fallback');
            this.simpleZipFallback(searchZip);
        }
        
        // Keep dropdown open
        this.open = true;
    }

    // Simple fallback when geocoding isn't available
    simpleZipFallback(searchZip) {
        // Check for exact ZIP match first
        const exactMatch = window.storeLocations.find(store => store.zip === searchZip);
        
        if (exactMatch && exactMatch.coordinates) {
            // console.log(`Found exact ZIP match: ${exactMatch.name}, using its coordinates`);
            // Use the exact match store's coordinates to find distances to all stores
            this.findNearestStore(exactMatch.coordinates.lat, exactMatch.coordinates.lng, false);
        } else {
            // No exact match or no coordinates - use region approximation
            const coords = this.getZipCodeRegion(searchZip);
            if (coords) {
                // console.log('Using regional approximation for ZIP:', searchZip);
                this.findNearestStore(coords.latitude, coords.longitude, false);
            } else {
                // console.log('No region found, showing all stores');
                // Just show all stores in their original order
                this.displayNearbyStores(window.storeLocations.map(store => ({ ...store, distance: 0 })));
            }
        }
    }

    // Fallback function to get approximate coordinates based on ZIP code region
    getZipCodeRegion(zipCode) {
        // ZIP code regions (first 3 digits) with approximate coordinates
        const zipRegions = {
            // Northeast
            '010': { latitude: 42.1085, longitude: -72.5829 }, // Springfield, MA
            '100': { latitude: 40.7128, longitude: -74.0060 }, // New York, NY

            // South - Georgia
            '300': { latitude: 33.7490, longitude: -84.3880 }, // Atlanta, GA
            '301': { latitude: 33.7490, longitude: -84.3880 }, // Atlanta, GA
            '302': { latitude: 33.7490, longitude: -84.3880 }, // Atlanta, GA  
            '303': { latitude: 33.7490, longitude: -84.3880 }, // Atlanta, GA
            '304': { latitude: 33.7490, longitude: -84.3880 }, // Atlanta, GA
            '305': { latitude: 33.7490, longitude: -84.3880 }, // Atlanta, GA
            '306': { latitude: 33.4484, longitude: -84.4550 }, // Macon, GA
            '310': { latitude: 33.4484, longitude: -84.4550 }, // Macon, GA
            '312': { latitude: 33.4484, longitude: -84.4550 }, // Macon, GA
            '313': { latitude: 32.8407, longitude: -83.6324 }, // Macon, GA
            '314': { latitude: 32.0809, longitude: -81.0912 }, // Savannah, GA
            '315': { latitude: 31.5785, longitude: -84.1557 }, // Albany, GA
            '316': { latitude: 31.5785, longitude: -84.1557 }, // South Georgia
            '317': { latitude: 31.5785, longitude: -84.1557 }, // South Georgia
            '318': { latitude: 32.4609, longitude: -84.9877 }, // Columbus, GA
            '319': { latitude: 32.4609, longitude: -84.9877 }, // Columbus, GA

            // South Carolina ZIP codes
            '290': { latitude: 34.0007, longitude: -81.0348 }, // Columbia, SC
            '291': { latitude: 34.0007, longitude: -81.0348 }, // Columbia, SC
            '292': { latitude: 34.0007, longitude: -81.0348 }, // Columbia, SC
            '293': { latitude: 34.9257, longitude: -81.0251 }, // Rock Hill, SC (Charlotte area)
            '294': { latitude: 32.7765, longitude: -79.9311 }, // Charleston, SC (294xx ZIPs)
            '295': { latitude: 33.6891, longitude: -78.8867 }, // Myrtle Beach, SC
            '296': { latitude: 34.8526, longitude: -82.3940 }, // Greenville, SC
            '297': { latitude: 34.1954, longitude: -79.7625 }, // Florence, SC
            '298': { latitude: 33.4709, longitude: -80.8543 }, // Aiken, SC
            '299': { latitude: 33.8361, longitude: -78.6469 }, // Beaufort, SC

            // North Carolina
            '270': { latitude: 36.0726, longitude: -79.7920 }, // Greensboro, NC
            '271': { latitude: 36.0998, longitude: -78.8785 }, // Winston-Salem, NC
            '272': { latitude: 36.0726, longitude: -79.7920 }, // Greensboro, NC
            '273': { latitude: 36.0726, longitude: -79.7920 }, // Greensboro, NC
            '274': { latitude: 36.0726, longitude: -79.7920 }, // Greensboro, NC
            '275': { latitude: 35.7796, longitude: -78.6382 }, // Raleigh, NC
            '276': { latitude: 35.7796, longitude: -78.6382 }, // Raleigh, NC
            '277': { latitude: 35.9940, longitude: -78.8986 }, // Durham, NC
            '278': { latitude: 35.9132, longitude: -79.0558 }, // Rocky Mount, NC
            '279': { latitude: 35.9132, longitude: -79.0558 }, // Eastern NC
            '280': { latitude: 35.2271, longitude: -80.8431 }, // Charlotte, NC
            '281': { latitude: 35.2271, longitude: -80.8431 }, // Charlotte, NC
            '282': { latitude: 35.2271, longitude: -80.8431 }, // Charlotte, NC
            '283': { latitude: 36.2168, longitude: -81.6745 }, // Boone, NC
            '284': { latitude: 35.2828, longitude: -81.1868 }, // Wilmington, NC
            '285': { latitude: 34.2257, longitude: -77.9447 }, // Winston-Salem, NC
            '286': { latitude: 35.5951, longitude: -82.5515 }, // Asheville, NC
            '287': { latitude: 35.5951, longitude: -82.5515 }, // Asheville, NC
            '288': { latitude: 35.5951, longitude: -82.5515 }, // Asheville, NC
            '289': { latitude: 35.5951, longitude: -82.5515 }, // Asheville, NC

            // Tennessee
            '370': { latitude: 36.1627, longitude: -86.7816 }, // Nashville, TN
            '371': { latitude: 36.1627, longitude: -86.7816 }, // Nashville, TN
            '372': { latitude: 36.1627, longitude: -86.7816 }, // Nashville, TN
            '373': { latitude: 35.0456, longitude: -85.3097 }, // Chattanooga, TN
            '374': { latitude: 35.0456, longitude: -85.3097 }, // Chattanooga, TN
            '375': { latitude: 35.9606, longitude: -83.9207 }, // Knoxville, TN
            '376': { latitude: 36.5298, longitude: -87.3595 }, // Johnson City, TN
            '377': { latitude: 35.9606, longitude: -83.9207 }, // Knoxville, TN
            '378': { latitude: 35.9606, longitude: -83.9207 }, // Knoxville, TN
            '379': { latitude: 35.9606, longitude: -83.9207 }, // Knoxville, TN
            '380': { latitude: 35.1495, longitude: -90.0490 }, // Memphis, TN
            '381': { latitude: 35.1495, longitude: -90.0490 }, // Memphis, TN
            '382': { latitude: 35.1495, longitude: -90.0490 }, // Memphis, TN
            '383': { latitude: 35.1495, longitude: -90.0490 }, // Memphis, TN
            '384': { latitude: 35.8456, longitude: -86.3903 }, // Columbia, TN
            '385': { latitude: 36.3134, longitude: -86.6100 }, // Cookeville, TN

            // Alabama
            '350': { latitude: 33.5186, longitude: -86.8104 }, // Birmingham, AL
            '351': { latitude: 33.5186, longitude: -86.8104 }, // Birmingham, AL
            '352': { latitude: 33.5186, longitude: -86.8104 }, // Birmingham, AL
            '354': { latitude: 32.3668, longitude: -86.2999 }, // Montgomery, AL
            '355': { latitude: 32.3668, longitude: -86.2999 }, // Montgomery, AL
            '356': { latitude: 34.7304, longitude: -86.5861 }, // Huntsville, AL
            '357': { latitude: 34.7304, longitude: -86.5861 }, // Huntsville, AL
            '358': { latitude: 34.7304, longitude: -86.5861 }, // Huntsville, AL
            '359': { latitude: 33.2098, longitude: -87.5692 }, // Tuscaloosa, AL
            '360': { latitude: 32.3668, longitude: -86.2999 }, // Montgomery, AL
            '361': { latitude: 32.3668, longitude: -86.2999 }, // Montgomery, AL
            '362': { latitude: 32.3792, longitude: -86.3077 }, // Dothan, AL
            '363': { latitude: 32.3792, longitude: -86.3077 }, // Dothan, AL
            '364': { latitude: 31.2232, longitude: -85.3905 }, // Mobile, AL
            '365': { latitude: 30.4213, longitude: -87.2169 }, // Mobile, AL
            '366': { latitude: 30.4213, longitude: -87.2169 }, // Mobile, AL
            '367': { latitude: 31.3113, longitude: -85.8265 }, // Enterprise, AL
            '368': { latitude: 32.4074, longitude: -87.0211 }, // Selma, AL
            '369': { latitude: 32.8407, longitude: -85.3956 }, // Phenix City, AL

            // Florida
            '320': { latitude: 32.0809, longitude: -81.0912 }, // Savannah, GA (North FL)
            '321': { latitude: 28.0345, longitude: -80.5887 }, // Melbourne, FL
            '322': { latitude: 30.3322, longitude: -81.6557 }, // Jacksonville, FL
            '323': { latitude: 29.6516, longitude: -82.3248 }, // Gainesville, FL
            '324': { latitude: 30.4383, longitude: -84.2807 }, // Tallahassee, FL
            '325': { latitude: 30.2121, longitude: -85.8550 }, // Panama City, FL
            '326': { latitude: 29.0299, longitude: -81.3029 }, // Daytona Beach, FL
            '327': { latitude: 28.5383, longitude: -81.3792 }, // Orlando, FL
            '328': { latitude: 28.5383, longitude: -81.3792 }, // Orlando, FL
            '329': { latitude: 27.9506, longitude: -81.8728 }, // Lakeland, FL
            '330': { latitude: 25.7617, longitude: -80.1918 }, // Miami, FL
            '331': { latitude: 25.7617, longitude: -80.1918 }, // Miami, FL
            '332': { latitude: 25.7617, longitude: -80.1918 }, // Miami, FL
            '333': { latitude: 25.7617, longitude: -80.1918 }, // Miami, FL
            '334': { latitude: 26.4615, longitude: -80.0728 }, // West Palm Beach, FL
            '335': { latitude: 27.9506, longitude: -82.4572 }, // Tampa, FL
            '336': { latitude: 27.9506, longitude: -82.4572 }, // Tampa, FL
            '337': { latitude: 27.7676, longitude: -82.6403 }, // St. Petersburg, FL
            '338': { latitude: 28.1036, longitude: -81.6026 }, // Lakeland, FL
            '339': { latitude: 26.1224, longitude: -81.7948 }, // Fort Myers, FL
            '340': { latitude: 27.9659, longitude: -82.8001 }, // Clearwater, FL
            '341': { latitude: 27.0979, longitude: -82.4543 }, // Sarasota, FL
            '342': { latitude: 27.3364, longitude: -82.5307 }, // Bradenton, FL
            '344': { latitude: 29.1872, longitude: -81.0484 }, // Ormond Beach, FL
            '346': { latitude: 27.9506, longitude: -82.4572 }, // Tampa, FL
            '347': { latitude: 28.0222, longitude: -81.7329 }, // Winter Haven, FL
            '349': { latitude: 26.4615, longitude: -80.0728 }, // West Palm Beach, FL

            // Midwest
            '430': { latitude: 39.9612, longitude: -82.9988 }, // Columbus, OH
            '480': { latitude: 42.3314, longitude: -83.0458 }, // Detroit, MI

            // West
            '840': { latitude: 40.7608, longitude: -111.8910 }, // Salt Lake City, UT
            '900': { latitude: 34.0522, longitude: -118.2437 }, // Los Angeles, CA
            '940': { latitude: 37.7749, longitude: -122.4194 }, // San Francisco, CA
        };

        // Get first 3 digits of ZIP code
        const zipPrefix = zipCode.substring(0, 3);

        // First try exact match
        if (zipRegions[zipPrefix]) {
            // console.log(`Found exact match for ZIP ${zipCode}: ${zipPrefix}`);
            return zipRegions[zipPrefix];
        }

        // If no exact match, find the closest region
        let closestRegion = '300'; // Default to Atlanta
        let smallestDifference = 999;

        for (const region in zipRegions) {
            const difference = Math.abs(parseInt(zipPrefix) - parseInt(region));
            if (difference < smallestDifference) {
                smallestDifference = difference;
                closestRegion = region;
            }
        }

        // console.log(`Using approximate region for ZIP ${zipCode}: ${closestRegion}`);
        return zipRegions[closestRegion];
    }


    calculateDistance(lat1, lon1, lat2, lon2) {
        return LocationUtils.calculateDistance(lat1, lon1, lat2, lon2);
    }
    
    // Helper method to escape HTML
    escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }
    
    // Format distance with better precision
    formatDistance(distance) {
        if (distance === undefined || distance === null || isNaN(distance)) {
            return '';
        }
        
        if (distance < 0.1) {
            return distance.toFixed(2) + ' miles'; // Show 2 decimal places for very small distances
        } else if (distance < 1) {
            return distance.toFixed(1) + ' miles'; // Show 1 decimal place for distances under 1 mile
        } else {
            return Math.round(distance * 10) / 10 + ' miles'; // Round to nearest 0.1 for larger distances
        }
    }
    
    // Simple improved displayNearbyStores method
    displayNearbyStores(storesWithDistance) {
        // Get the existing stores list
        const storesList = this.querySelector('[data-stores-list]');
        if (!storesList) {
            // console.error('Store list not found');
            return;
        }

        // Clear the list completely - we'll rebuild it from scratch
        storesList.innerHTML = '';
        
        // Use a Set to track which stores we've already added (by store ID)
        const addedStoreIds = new Set();
        
        // Sort stores by distance
        const sortedStores = [...storesWithDistance].sort((a, b) => a.distance - b.distance);
        
        // Filter out duplicates while maintaining sort order
        const uniqueStores = sortedStores.filter(store => {
            if (addedStoreIds.has(store.id)) {
                // console.log(`Skipping duplicate store: ${store.name} (${store.id})`);
                return false;
            }
            addedStoreIds.add(store.id);
            return true;
        });
        
        // For ZIP searches, format distance appropriately
        const isZipSearch = uniqueStores.length > 0 && Number.isInteger(uniqueStores[0].distance);
        
        if (isZipSearch) {
            // console.log('Displaying stores sorted by ZIP distance:', uniqueStores.slice(0, 5).map(s => `${s.name} (${s.zip})`));
        } else {
            // console.log('Displaying stores sorted by distance:', uniqueStores.slice(0, 5).map(s => `${s.name}: ${s.distance.toFixed(2)} miles`));
        }
        
        // Build the list from scratch using the sorted, deduplicated store data
        uniqueStores.forEach((store, index) => {
            const storeItem = document.createElement('li');
            storeItem.className = 'store-picker__store';
            storeItem.dataset.storeId = store.id;
            storeItem.dataset.storeName = store.name;
            if (store.coordinates) {
                storeItem.dataset.latitude = store.coordinates.lat;
                storeItem.dataset.longitude = store.coordinates.lng;
            }
            
            // Format distance display
            let distanceDisplay = '';
            if (isZipSearch) {
                // For ZIP searches, show the ZIP code instead of distance
                distanceDisplay = `ZIP: ${store.zip}`;
            } else if (typeof store.distance === 'number' && !isNaN(store.distance)) {
                distanceDisplay = this.formatDistance(store.distance);
            }
            
            // Create the store content
            storeItem.innerHTML = `
                <div class="store-picker__store-header">
                    <h3 class="h4">${this.escapeHtml(store.name)}</h3>
                    <button class="store-fav-selector btn btn--secondary" 
                            type="button" 
                            aria-label="Set ${this.escapeHtml(store.name)} as your location" 
                            data-store-id="${store.id}">
                        <span class="store-fav-selector__label">Set <span class="visually-hidden">${this.escapeHtml(store.name)}</span> as my store</span>
                        <span class="store-fav-selector__label store-fav-selector__label--favorited">My store</span>
                    </button>
                    ${distanceDisplay ? `<div class="store-distance-indicator" style="font-size: 12px; color: #666; text-align:right;">${distanceDisplay}</div>` : ''}
                </div>

                <address class="store-picker__store-address">
                    ${store.address ? `${this.escapeHtml(store.address)}<br>` : ''}
                    ${store.city ? `${this.escapeHtml(store.city)}, ` : ''}
                    ${store.state ? `${this.escapeHtml(store.state)} ` : ''}
                    ${store.zip || ''}
                    ${store.phone ? `<p>${this.escapeHtml(store.phone)}</p>` : ''}
                </address>
            `;
            
            // Add border style
            storeItem.style.borderBottom = '1px solid rgba(0,0,0,0.1)';
            storeItem.style.paddingBottom = '15px';
            storeItem.style.marginBottom = '15px';
            
            // Remove border from last item
            if (index === uniqueStores.length - 1) {
                storeItem.style.borderBottom = 'none';
            }
            
            storesList.appendChild(storeItem);
        });
        
        // Update the location display
        this.updateLocationDisplay();
        
        // Move buttons below addresses
        this.moveButtonsBelowAddress();
    }
    
    // Simple method to restore original order
    resetStoreView() {
        const storesList = this.querySelector('[data-stores-list]');
        if (!storesList || this.originalStoreOrder.length === 0) return;
        
        // Clear saved location but don't update display yet (to avoid flicker)
        this.currentSearchLocation = '';
        
        // Get current store elements
        const currentStores = {};
        storesList.querySelectorAll('li[data-store-id]').forEach(item => {
            const clone = item.cloneNode(true);
            // Remove any distance indicators
            const indicator = clone.querySelector('.store-distance-indicator');
            if (indicator) indicator.remove();
            // Remove inline styles
            clone.style.borderBottom = '';
            clone.style.paddingBottom = '';
            clone.style.marginBottom = '';
            currentStores[item.dataset.storeId] = clone;
        });
        
        // Clear and rebuild in original order
        storesList.innerHTML = '';
        this.originalStoreOrder.forEach((storeId, index) => {
            const storeItem = currentStores[storeId];
            if (storeItem) {
                // Add border bottom style to each li instead of using dividers
                storeItem.style.borderBottom = '1px solid rgba(0,0,0,0.1)';
                storeItem.style.paddingBottom = '15px';
                storeItem.style.marginBottom = '15px';
                
                // Remove border from last item
                if (index === this.originalStoreOrder.length - 1) {
                    storeItem.style.borderBottom = 'none';
                }
                
                storesList.appendChild(storeItem);
            }
        });
        
        // Now update the display
        this.updateLocationDisplay();
        
        // Move buttons below addresses
        this.moveButtonsBelowAddress();
    }

    // Enhanced version of the showLocationError method
    showLocationError(message) {
        // console.error('Location error:', message);

        // Find error element with more robust selector
        let errorElement = document.querySelector('.location-error');

        if (!errorElement) {
            // console.error('Error container not found, creating one');
            const locationFinder = document.querySelector('.location-finder');
            
            if (locationFinder) {
                errorElement = document.createElement('div');
                errorElement.className = 'location-error';
                locationFinder.appendChild(errorElement);
            } else {
                // console.error('Cannot find location-finder to append error element');
                return;
            }
        }

        errorElement.innerHTML = `
            <div class="error-message" style="background-color: #fff2f2; border-left: 3px solid #ff5252; color: #d32f2f; padding: 10px; display: flex; align-items: center; gap: 8px; font-size: 13px; border-radius: 3px; margin-top: 10px;">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="12"></line>
                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
                ${message}
            </div>
        `;

        // Auto-hide after 5 seconds
        setTimeout(() => {
            if (errorElement) {
                errorElement.innerHTML = '';
            }
        }, 5000);
    }

    // Diagnostic function
    diagnoseStorePicker() {
        // console.group('Store Picker Diagnostics');
        // console.log('Store Locations:', this.storeLocations.length);
        // console.log('Original Store Order:', this.originalStoreOrder.length);
        // console.log('Current Search Location:', this.currentSearchLocation);
        // console.log('User Store:', this.userStore);
        // console.log('localStorage:', {
        //     userStoreId: localStorage.getItem('userStoreId'),
        //     userStoreName: localStorage.getItem('userStoreName'),
        //     userSearchLocation: localStorage.getItem('userSearchLocation'),
        //     userSearchCoordinates: localStorage.getItem('userSearchCoordinates')
        // });
        
        const storeElements = this.querySelectorAll('li[data-store-id]');
        // console.log('Store Elements:', storeElements.length);
        // console.groupEnd();
    }

    // handlers
    handleBodyClick(evt) {
        const target = evt.target;
        if (target.closest('store-picker summary')) {
            this.open = (this.open === true) ? false : true;
            return;
        }
        if (target.closest('store-picker button.store-fav-selector')) {
            let storeItem = target.closest('li');
            if (!storeItem) return;
            let favStoreId = storeItem.dataset.storeId;
            let favStoreName = storeItem.dataset.storeName;
            // console.log('Store selected from picker:', favStoreId, favStoreName);
            this.userStore = favStoreId;

            // Close the dropdown after selection
            this.open = false;

            // Update ZIP input with selected store's ZIP code
            const selectedStore = window.storeLocations && window.storeLocations.find(s => s.id === favStoreId);
            if (selectedStore && selectedStore.zip) {
                const zipInput = document.querySelector('.zip-code-input');
                if (zipInput) {
                    zipInput.value = selectedStore.zip;
                }
            }

            return;
        }
        if (!target.closest('store-picker') && !target.closest('.location-finder')) {
            this.open = false;
            return;
        }
    }
    
    handleKeyup(evt) {
        if (evt.code.toUpperCase() === 'ESCAPE') this.open = false;
    }
    
    // getters
    get open() {
        return this.hasAttribute('open');
    }
    
    get userStore() {
        return this.getAttribute('user-store');
    }
    
    // setters
    set open(bool) {
        if (bool) {
            // Only open if this is not an initial load or auto request
            if (!this.isInitialLoad) {
                this.setAttribute('open', '');
            } else {
                // console.log('Prevented opening dropdown during initial load');
            }
        } else {
            this.removeAttribute('open');
        }
    }
    
    set userStore(storeId) {
        this.setAttribute('user-store', storeId);
    }
    
    // methods
    hide() {
        setTimeout(() => {
            this.elements.details.removeAttribute('open');
        });
        if (this.open) this.open = false;
        document.body.removeEventListener('keyup', this.onKeyup);
        if (typeof removeTrapFocus === 'function') {
            removeTrapFocus(this.focusElement);
        }
    }
    
    show(focusElement) {
        this.focusElement = focusElement;
        
        // Only show if this is not an initial load
        if (!this.isInitialLoad) {
            setTimeout(() => {
                this.elements.details.setAttribute('open', '');
                // Move buttons below addresses when dropdown opens
                this.moveButtonsBelowAddress();
            });
        } else {
            // console.log('Prevented showing dropdown during initial load');
        }
        
        if (!this.open && !this.isInitialLoad) this.open = true;
        document.body.addEventListener('keyup', this.onKeyup);
        if (typeof trapFocus === 'function') {
            trapFocus(this);
        }
    }
    
    assignStore() {
        if (!this.userStore) return;
        let favStore = this.querySelector(`li[data-store-id="${this.userStore}"]`);
        if (!favStore) {
            // console.log('Store not found in picker:', this.userStore);
            return;
        }

        // Just update UI - don't call setStore here since this is triggered BY the store:selected event
        // Calling setStore here would cause a circular loop
        const storeName = favStore.dataset.storeName || "Your Store";

        let favBtn = this.querySelector(`button[data-store-id="${this.userStore}"]`);
        if (favBtn) {
            favBtn.classList.remove('btn--secondary');
            favBtn.classList.add('store-fav-selector--enabled');
        }
        if (this.elements.selected) {
            this.elements.selected.innerText = storeName;
        }
        // console.log('Store assigned in picker:', this.userStore, storeName);
    }
    
    arrangeStores() {
        if (!this.userStore) return;
        const storesList = this.querySelector('[data-stores-list]');
        let favStore = this.querySelector(`[data-store-id="${this.userStore}"]`);
        if (storesList && favStore) {
            storesList.prepend(favStore);
            // console.log('Arranged stores with selected store first');
            
            // Move buttons below addresses after arranging
            this.moveButtonsBelowAddress();
        }
    }
    
    clearStore() {
        this.querySelectorAll('.store-fav-selector').forEach(btn => {
            btn.classList.add('btn--secondary');
            btn.classList.remove('store-fav-selector--enabled');
        });
    }
    
    static get observedAttributes() {
        return ['open', 'user-store'];
    }
    
    attributeChangedCallback(name, oldValue, newValue) {
        // console.log(`Attribute ${name} changed from ${oldValue} to ${newValue}`);
        switch (name) {
            case 'open':
                // Check if elements are initialized before proceeding
                if (!this.elements || !this.elements.summary) {
                    // console.log('Elements not initialized yet, skipping open attribute handling');
                    return;
                }
                
                this.arrangeStores();
                if (newValue === null) {
                    this.hide();
                } else {
                    // Only show if not during initial load
                    if (!this.isInitialLoad) {
                        this.show(this.elements.summary);
                    } else {
                        // console.log('Prevented opening dropdown during attribute change in initial load');
                    }
                }
                break;
            case 'user-store':
                this.clearStore();
                this.assignStore();
                // When store changes, ensure buttons are in correct position
                setTimeout(() => {
                    this.moveButtonsBelowAddress();
                }, 100);
                break;
        }
    }
}

// Make storeLocations array global if it exists in store-locator.js but isn't exported
document.addEventListener('DOMContentLoaded', () => {
    // Check if there's a storeLocations variable defined in any scope
    if (typeof storeLocations !== 'undefined' && !window.storeLocations) {
        window.storeLocations = storeLocations;
        // console.log('Made storeLocations globally available');
    } else {
        // console.log('Checking for window.storeLocations:', !!window.storeLocations);
    }

    // CSS for spinner animation
    const styleElement = document.createElement('style');
    styleElement.textContent = `
    @keyframes spinner {
      to {
        transform: rotate(360deg);
      }
    }
    `;
    document.head.appendChild(styleElement);
    
    // Run diagnostics
    setTimeout(() => {
        try {
            const storePicker = document.querySelector('store-picker');
            if (storePicker) {
                storePicker.diagnoseStorePicker();
            }
        } catch (e) {
            // console.error('Error in store picker diagnostics:', e);
        }
    }, 2000);
});

// Listen for store selections - update ALL store-picker elements on the page
document.addEventListener('store:selected', (e) => {
    console.log('[7] store:selected event received:', e.detail);
    if (!e.detail || !e.detail.id) {
        console.log('[7] No detail or id, returning early');
        return;
    }

    const storePickers = document.querySelectorAll('store-picker');
    console.log('[7] Found', storePickers.length, 'store-picker elements');

    storePickers.forEach(storePicker => {
        // Update attribute if different - this triggers attributeChangedCallback which calls clearStore() and assignStore()
        if (storePicker.getAttribute('user-store') !== e.detail.id) {
            storePicker.setAttribute('user-store', e.detail.id);
        }
        // Always update the display text with store name from event
        if (e.detail.name) {
            const selectedDisplay = storePicker.querySelector('strong');
            if (selectedDisplay) {
                selectedDisplay.innerText = e.detail.name;
            }
        }
    });
});

// Listen for location changes - sync ZIP input fields across the page
document.addEventListener('location:changed', (e) => {
    const zipCode = e.detail?.zipCode;
    // Only process if it's a valid ZIP code
    if (zipCode && /^\d{5}$/.test(zipCode)) {
        // Update ALL ZIP input fields on the page (header, sidebar, etc.)
        const zipInputs = document.querySelectorAll('.zip-code-input');
        zipInputs.forEach(input => {
            input.value = zipCode;
        });
    }
});

// NOTE: Removed legacy code that was overwriting valid ZIP with "your current location" string.
// Geolocation ZIP is now properly set at line 802 using the nearest store's ZIP.

customElements.define('store-picker', StorePicker); 